import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";
import type { Page } from "./page";

/**
 * Admin token API (`/admin/api/v1/tokens`) — ADMIN surface pro SPA přehled +
 * detail tokenů. Odlišné od zákaznického integračního API `/api/v1/tokens`
 * (`TokenController`). Běží na API chainu (JWT cookie). List vrací souhrny
 * (vč. PAAT profilu a stavu); detail přidává konektor-specifické atributy.
 */
export interface TokenSummary {
  tokenId: string;
  username: string;
  state: string;
  stateReason: string | null;
  authServerType: string;
  activationGuid: string | null;
  createdAt: string | null;
  activatedAt: string | null;
  expiresAt: string | null;
  tags: string[] | null;
  paatProfileId: string | null;
  paatProfilePlatform: string | null;
  /**
   * Které operace stav tokenu dovoluje — počítá **server** (`TokenActions`),
   * klient pravidlo nezná. Hodnota je důvod zákazu, `null` = povoleno.
   */
  actions: TokenActionAvailability;
}

export interface TokenDetail extends TokenSummary {
  licenceId?: string | null;
  instanceSeq?: string | null;
  password?: string | null;
  pushIdentifier?: string | null;
  deviceFingerprint?: string | null;
  updatedAt?: string | null;
  paatProfileName?: string | null;
  paatDeviceModel?: string | null;
  activationEventCount?: number | null;
  connectorAttributes?: Record<string, string> | null;
  // SDK secrets (base64 pro byte[] pole) — jen testovací účely
  deviceEncryptionSalt?: string | null;
  devicePasswordForCommon?: string | null;
  deviceSecretPassword?: string | null;
  asymTokenData?: string | null;
  /**
   * Režim šifrování klíčů zaznamenaný na tokenu při zápisu klíčového materiálu —
   * ne aktuální nastavení serveru, to se od aktivace mohlo přepnout.
   * `null` = token vznikl dřív, než se režim zaznamenával.
   */
  keyStorageFormat?: TokenKeyStorageFormat | null;
}

/** Režim šifrování klíčů v úložišti tokenu (protějšek `ETTS_TOKEN_STORAGE_FORMAT`). */
export type TokenKeyStorageFormat = "ETTS" | "SECURE";

/**
 * Proč akce nad tokenem nejde spustit (`null` = jde). Kódy posílá server
 * v poli `actions`; text se skládá až tady přes i18n.
 */
export type TokenActionBlock = "DELETED" | "NOT_ACTIVATED" | "NOT_ACTIVE";

/** Matice dostupnosti akcí tak, jak ji spočítal server. */
export type TokenActionAvailability = Record<
  "SYNC" | "TAGS" | "EXPIRATION" | "PAAT" | "GENERATE_OTP" | "CHANGE_PASSWORD" | "DEACTIVATE" | "DELETE",
  TokenActionBlock | null
>;

/** i18n klíč s vysvětlením, proč je akce zakázaná (do tooltipu). */
export function tokenActionBlockKey(reason: TokenActionBlock): string {
  switch (reason) {
    case "DELETED":
      return "tokens.action.blocked.deleted";
    case "NOT_ACTIVATED":
      return "tokens.action.blocked.notActivated";
    case "NOT_ACTIVE":
      return "tokens.action.blocked.notActive";
  }
}

export interface TokenFilter {
  state?: string;
  authServerType?: string;
  /** Podřetězec Token ID, case-insensitive (serverová strana). */
  tokenId?: string;
  username?: string;
  tag?: string;
  paatProfileId?: string;
}

/**
 * Filtr bez prázdných hodnot.
 *
 * Sdílené se hromadnými akcemi: tělo hromadného požadavku musí nést **přesně** ten filtr,
 * který dal seznam. Kdyby se skládal jinde a jinak, akce by se trefila do jiné množiny,
 * než jakou má uživatel před očima — u mazání ta nejnebezpečnější možná chyba.
 */
export function nonEmptyFilter(filter: TokenFilter): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(filter)) {
    if (v != null && String(v).trim() !== "") out[k] = String(v).trim();
  }
  return out;
}

/** Neprázdné filtry → query string (a stabilní queryKey). */
function filterParams(filter: TokenFilter): string {
  return new URLSearchParams(nonEmptyFilter(filter)).toString();
}

/**
 * Stránka seznamu tokenů. Filtruje, řadí i stránkuje **server** — tokenů jsou
 * běžně tisíce (zátěžové testy) a stahovat je celé kvůli jedné obrazovce nejde.
 *
 * `keepPreviousData` drží předchozí stránku na obrazovce, dokud nedorazí další,
 * takže tabulka mezi stránkami neproblikne na prázdno.
 */
export function useTokens(page: number, size: number, sort: TokenSort, filter: TokenFilter = {}) {
  const qs = filterParams(filter);
  const sortValue = sortParam(sort);
  return useQuery({
    queryKey: ["tokens", "list", page, size, sortValue, qs],
    queryFn: () =>
      apiFetch<Page<TokenSummary>>(
        `/admin/api/v1/tokens?page=${page}&size=${size}&sort=${encodeURIComponent(sortValue)}${qs ? `&${qs}` : ""}`,
      ),
    placeholderData: keepPreviousData,
  });
}

/** Souhrnné počty tokenů podle stavu a konektoru (pro Přehled). */
export interface TokenStats {
  total: number;
  byState: Record<string, number>;
  byAuthServerType: Record<string, number>;
}

/**
 * Počty pro Přehled. Agreguje databáze — Přehled dřív stahoval celý seznam tokenů
 * a pět čísel si z něj počítal v prohlížeči.
 */
export function useTokenStats() {
  return useQuery({
    queryKey: ["tokens", "stats"],
    queryFn: () => apiFetch<TokenStats>("/admin/api/v1/tokens/stats"),
  });
}

/**
 * Našeptávač tokenů pro výběr v testovacích nástrojích — vrací jen prvních
 * `limit` shod, hledá se v Token ID na serveru.
 */
export function searchTokens(params: {
  query: string;
  authServerType?: string | null;
  activeOnly?: boolean;
  limit?: number;
}): Promise<TokenSummary[]> {
  const p = new URLSearchParams({ page: "0", size: String(params.limit ?? 20), sort: "createdAt,desc" });
  if (params.query.trim()) p.set("tokenId", params.query.trim());
  if (params.authServerType) p.set("authServerType", params.authServerType);
  if (params.activeOnly) p.set("state", "ACTIVE");
  return apiFetch<Page<TokenSummary>>(`/admin/api/v1/tokens?${p}`).then((r) => r.content);
}

export function useToken(tokenId: string) {
  return useQuery({
    queryKey: ["tokens", "detail", tokenId],
    queryFn: () => apiFetch<TokenDetail>(`/admin/api/v1/tokens/${encodeURIComponent(tokenId)}`),
    enabled: !!tokenId,
  });
}

/** Invaliduje seznam i detail po mutaci tokenu. */
function useTokenMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tokens"] }),
  });
}

export function useDeactivateToken() {
  return useTokenMutation((tokenId: string) =>
    apiFetch(`/admin/api/v1/tokens/${encodeURIComponent(tokenId)}/deactivate`, { method: "POST" }),
  );
}

export function useDeleteToken() {
  return useTokenMutation((tokenId: string) =>
    apiFetch(`/admin/api/v1/tokens/${encodeURIComponent(tokenId)}`, { method: "DELETE" }),
  );
}

export function useSetTokenTags() {
  return useTokenMutation(({ tokenId, tags }: { tokenId: string; tags: string[] }) =>
    apiFetch(`/admin/api/v1/tokens/${encodeURIComponent(tokenId)}/tags`, { method: "PUT", json: { tags } }),
  );
}

export interface GenerateOtpResult {
  success: boolean;
  otp: string | null;
  message?: string | null;
  otpExpiresAt?: string | null;
}

/** Vygeneruje OTP z hesla (admin twin). Bez invalidace — nic nemění. */
export function useGenerateOtp() {
  return useMutation({
    mutationFn: ({ tokenId, password }: { tokenId: string; password: string }) =>
      apiFetch<GenerateOtpResult>(`/admin/api/v1/tokens/${encodeURIComponent(tokenId)}/generate-otp`, {
        method: "POST",
        json: { password },
      }),
  });
}

/** Změna hesla tokenu (ověří staré). Invaliduje detail. */
export function useChangePassword() {
  return useTokenMutation(({ tokenId, oldPassword, newPassword }: { tokenId: string; oldPassword: string; newPassword: string }) =>
    apiFetch(`/admin/api/v1/tokens/${encodeURIComponent(tokenId)}/change-password`, {
      method: "POST",
      json: { oldPassword, newPassword },
    }),
  );
}

export function useSetTokenExpiration() {
  return useTokenMutation(
    ({ tokenId, expiresAt, expiresInSeconds }: { tokenId: string; expiresAt: string | null; expiresInSeconds?: number | null }) =>
      apiFetch(`/admin/api/v1/tokens/${encodeURIComponent(tokenId)}/expiration`, {
        method: "PUT",
        json: { expiresAt, expiresInSeconds: expiresInSeconds ?? null },
      }),
  );
}

export function useChangePaatProfile() {
  return useTokenMutation(({ tokenId, paatProfileId }: { tokenId: string; paatProfileId: string }) =>
    apiFetch(`/admin/api/v1/tokens/${encodeURIComponent(tokenId)}/paat-profile`, {
      method: "PUT",
      json: { paatProfileId },
    }),
  );
}

export interface StateSyncResult {
  oldState: string;
  newState: string;
  changed: boolean;
}

export function useSyncTokenState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) =>
      apiFetch<StateSyncResult>(`/admin/api/v1/tokens/${encodeURIComponent(tokenId)}/sync-state`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tokens"] }),
  });
}

/** Stavy tokenu (InstanceState) pro filtr. */
export const TOKEN_STATES = [
  "WAITING_FOR_ACTIVATION",
  "ACTIVE",
  "TEMPORARY_BLOCKED",
  "BLOCKED",
  "DELETED",
] as const;

/** Mapování stavu tokenu na UUI Badge barvu. */
/**
 * Barva odznaku režimu úložiště klíčů. `ETTS` není chyba ani varování — je to
 * výchozí režim pro výkonnostní testy, proto neutrální modrá.
 */
export function keyStorageFormatColor(format: TokenKeyStorageFormat): "success" | "blue" {
  return format === "SECURE" ? "success" : "blue";
}

export function tokenStateColor(state: string): "success" | "gray" | "warning" | "error" {
  switch (state) {
    case "ACTIVE":
      return "success";
    case "DELETED":
      return "gray";
    case "WAITING_FOR_ACTIVATION":
      return "warning";
    case "TEMPORARY_BLOCKED":
    case "BLOCKED":
      return "error";
    default:
      return "gray";
  }
}

/**
 * Sloupce seznamu tokenů, které lze řadit. Musí odpovídat whitelistu na serveru
 * (`AdminTokenQueryService.SORTABLE`) — neznámý sloupec server ignoruje.
 */
export type TokenSortColumn = "tokenId" | "username" | "state" | "authServerType" | "createdAt" | "activatedAt" | "expiresAt";

export interface TokenSort {
  column: TokenSortColumn;
  direction: "ascending" | "descending";
}

/** `TokenSort` → parametr `sort` Spring Data (`vlastnost,směr`). */
export function sortParam(sort: TokenSort): string {
  return `${sort.column},${sort.direction === "descending" ? "desc" : "asc"}`;
}
